import React, {useEffect, useMemo, useState} from "react";
import {useForm} from "react-hook-form";

import './style.css'

import {useSocialLoginMutation, useUserSignInMutation, useUserSignupMutation} from "../../redux/service/userService";
import {toast} from "react-toastify";
import {setUserDetails, upgradeToPro} from "../../redux/userSlice";
import {useDispatch} from "react-redux";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faEye,
    faEyeSlash,
    faSpinner,
    faCircleExclamation,
    faXmark,
} from "@fortawesome/free-solid-svg-icons";
import {faFacebookF} from "@fortawesome/free-brands-svg-icons";
import {signInWithPopup, GoogleAuthProvider, FacebookAuthProvider} from "firebase/auth";
import {auth, googleProvider, facebookProvider} from "../../firebase/firebase";
import AppLogo from "../../assets/images/lankalearners.png";

const GoogleIcon = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
        <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.9c1.7-1.57 2.7-3.88 2.7-6.62z"/>
        <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.26c-.8.54-1.84.86-3.06.86-2.35 0-4.34-1.59-5.05-3.72H.94v2.33A9 9 0 0 0 9 18z"/>
        <path fill="#FBBC05" d="M3.95 10.7a5.4 5.4 0 0 1 0-3.4V4.97H.94a9 9 0 0 0 0 8.06l3.01-2.33z"/>
        <path fill="#EA4335" d="M9 3.58c1.32 0 2.51.46 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .94 4.97L3.95 7.3C4.66 5.17 6.65 3.58 9 3.58z"/>
    </svg>
);

export default function AuthModalRHF({
                                         open,
                                         onClose,
                                         onAuth, // async ({ mode, email, password, name }) => void
                                         initialMode = "signin",
                                     }) {
    const dispatch = useDispatch();


    const [mode, setMode] = useState(initialMode);
    const [showPw, setShowPw] = useState(false);
    const [showConfirmPw, setShowConfirmPw] = useState(false);
    const [serverError, setServerError] = useState("");
    const [socialLoading, setSocialLoading] = useState(null); // "google" | "facebook" | null

    const title = useMemo(() => (mode === "signin" ? "Welcome back!" : "Create your account"), [mode]);
    const subtitle = useMemo(
        () => (mode === "signin" ? "Please enter your login details." : "It only takes a minute to get started."),
        [mode]
    );

    const [userSignup, {isLoading: isSignUpLoading}] = useUserSignupMutation();
    const [userSigIn, {isLoading: isSignInLoading}] = useUserSignInMutation();
    const [socialLogin] = useSocialLoginMutation();

    const formLoading = mode === "signup" ? isSignUpLoading : isSignInLoading;
    const anyLoading = formLoading || !!socialLoading;


    const {
        register,
        handleSubmit,
        reset,
        watch,
        formState: {errors},
        clearErrors,
    } = useForm({
        defaultValues: {
            name: "",
            email: "",
            phoneNumber: '',
            password: "",
            confirm: "",
        },
        mode: "onSubmit",
    });

    const passwordValue = watch("password");

    useEffect(() => {
        if (!open) return;

        setMode(initialMode);
        setServerError("");
        setShowPw(false);
        setShowConfirmPw(false);
        reset({name: "", email: "", password: "", confirm: ""});

    }, [open, initialMode, onClose, reset]);

    const switchMode = (m) => {
        setMode(m);
        setServerError("");
        setShowPw(false);
        setShowConfirmPw(false);
        clearErrors();
        reset({name: "", email: "", password: "", confirm: ""});
    };

    const getErrorMessage = (err) => {
        // RTK Query fetchBaseQuery error shape: { status, data }
        if (err?.data?.message) return err.data.message;

        // Some APIs use different keys
        if (typeof err?.data === "string") return err.data;

        // SerializedError shape
        if (err?.error) return err.error;
        if (err?.message) return err.message;

        return "Something went wrong. Please try again.";
    };

    const onSubmit = async (data) => {

        if (mode === "signup") {

            const obj = {
                email: data?.email,
                password: data.password,
                name: data.name,
                phoneNumber: data.phoneNumber,
            };

            try {
                const result = await userSignup(obj).unwrap();
                toast.success('Successfully Sign up ...!')
                reset()
                switchMode("signin")

                return result;
            } catch (error) {
                toast.error(getErrorMessage(error));
                return;
            }
        } else {
            const obj = {
                email: data?.email,
                password: data?.password
            };

            try {
                const result = await userSigIn(obj).unwrap();
                toast.success('Successfully Sign IN ...!')

                localStorage.setItem('token', result?.token);
                if (result?.refreshToken) {
                    localStorage.setItem('refreshToken', result.refreshToken);
                }
                const userToStore = {
                    email: result?.user.email,
                    name: result?.user.name,
                    phoneNumber: result?.user.phoneNumber,
                    userId: result?.userId,
                };
                dispatch(setUserDetails(userToStore));
                if (result?.user.isProUser === true) {

                    dispatch(upgradeToPro());
                }
                reset()
                onClose()
                return result;
            } catch (error) {
                toast.error(getErrorMessage(error));
                return;
            }
        }


    };

    const handleSocialSignIn = async (provider, name) => {
        setSocialLoading(name);
        try {
            const result = await signInWithPopup(auth, provider);

            let payload;
            if (name === "google") {
                const credential = GoogleAuthProvider.credentialFromResult(result);
                if (!credential?.idToken) {
                    throw new Error("Could not retrieve Google credential. Please try again.");
                }
                payload = {provider: "google", idToken: credential.idToken};
            } else {
                const credential = FacebookAuthProvider.credentialFromResult(result);
                if (!credential?.accessToken) {
                    throw new Error("Could not retrieve Facebook credential. Please try again.");
                }
                payload = {provider: "facebook", accessToken: credential.accessToken};
            }

            const data = await socialLogin(payload).unwrap();

            localStorage.setItem("token", data?.token);
            if (data?.refreshToken) {
                localStorage.setItem("refreshToken", data.refreshToken);
            }

            const userToStore = {
                userId: data?.userId,
                email: data?.user?.email,
                name: data?.user?.name,
                phoneNumber: data?.user?.phoneNumber,
                isPro: data?.user?.isProUser,
            };
            dispatch(setUserDetails(userToStore));
            if (data?.user?.isProUser === true) {
                dispatch(upgradeToPro());
            }

            toast.success(`Successfully signed in with ${name === "google" ? "Google" : "Facebook"}!`);
            reset();
            onClose();
        } catch (error) {
            toast.error(getErrorMessage(error) || `${name === "google" ? "Google" : "Facebook"} sign-in failed.`);
        } finally {
            setSocialLoading(null);
        }
    };

    const handleGoogleSignIn = () => handleSocialSignIn(googleProvider, "google");
    const handleFacebookSignIn = () => handleSocialSignIn(facebookProvider, "facebook");

    const handleForgotPassword = () => {
        toast.info("Password reset isn't available yet. Please contact support.");
    };


    if (!open) return null;

    return (
        <div className="authOverlay" onMouseDown={onClose}>
            <div className="authModal" onMouseDown={(e) => e.stopPropagation()}>
                <button type="button" className="authClose" onClick={onClose} aria-label="Close" disabled={anyLoading}>
                    <FontAwesomeIcon icon={faXmark}/>
                </button>

                <div className="authBrand">
                    <div className="authBrandIcon">
                        <img src={AppLogo} alt="Lanka Learners" className="authBrandLogo"/>
                    </div>
                </div>

                <div className="authTabs">
                    <button
                        type="button"
                        className={`authTab ${mode === "signin" ? "authTabActive" : ""}`}
                        onClick={() => switchMode("signin")}
                        disabled={anyLoading}
                    >
                        Sign in
                    </button>
                    <button
                        type="button"
                        className={`authTab ${mode === "signup" ? "authTabActive" : ""}`}
                        onClick={() => switchMode("signup")}
                        disabled={anyLoading}
                    >
                        Sign up
                    </button>
                </div>

                <h2 className="authTitle">{title}</h2>
                <p className="authSubtitle">{subtitle}</p>

                {serverError ? (
                    <div className="authError">
                        <FontAwesomeIcon icon={faCircleExclamation}/>
                        <span>{serverError}</span>
                    </div>
                ) : null}

                <form onSubmit={handleSubmit(onSubmit)} className="authForm">
                    {mode === "signup" && (
                        <div className="authRow">
                            <label className={`authField ${errors.name ? "authFieldInvalid" : ""}`}>
                                <span>Name</span>
                                <input
                                    placeholder="Your name"
                                    disabled={anyLoading}
                                    {...register("name", {
                                        required: "Name is required.",
                                        minLength: {value: 2, message: "Name must be at least 2 characters."},
                                    })}
                                />
                                {errors.name ? <p className="authFieldError">{errors?.name.message}</p> : null}
                            </label>

                            <label className={`authField ${errors.phoneNumber ? "authFieldInvalid" : ""}`}>
                                <span>Phone Number</span>
                                <input
                                    placeholder="+9477*******"
                                    disabled={anyLoading}
                                    {...register("phoneNumber", {
                                        required: "Phone number is required",
                                        // simple phone validation (supports + and digits)
                                        pattern: {
                                            value: /^\+?[0-9]{9,15}$/,
                                            message: "Enter a valid phone number (ex: +94771234567)",
                                        },
                                    })}
                                />
                                {errors.phoneNumber && (
                                    <p className="authFieldError">{errors.phoneNumber.message}</p>
                                )}
                            </label>
                        </div>
                    )}

                    <label className={`authField ${errors.email ? "authFieldInvalid" : ""}`}>
                        <span>Email</span>
                        <input
                            placeholder="johnwick123@gmail.com"
                            disabled={anyLoading}
                            {...register("email", {
                                required: "Email is required.",
                                pattern: {value: /^\S+@\S+\.\S+$/, message: "Enter a valid email."},
                            })}
                        />
                        {errors.email ? <p className="authFieldError">{errors.email.message}</p> : null}
                    </label>

                    {mode === "signup" ? (
                        <div className="authRow">
                            <label className={`authField ${errors.password ? "authFieldInvalid" : ""}`}>
                                <span>Password</span>
                                <div className="authInputRow">
                                    <input
                                        type={showPw ? "text" : "password"}
                                        placeholder="••••••••••••"
                                        disabled={anyLoading}
                                        {...register("password", {
                                            required: "Password is required.",
                                            minLength: {value: 6, message: "Password must be at least 6 characters."},
                                        })}
                                    />
                                    <button
                                        type="button"
                                        className="authPwToggle"
                                        onClick={() => setShowPw((s) => !s)}
                                        disabled={anyLoading}
                                        aria-label={showPw ? "Hide password" : "Show password"}
                                    >
                                        <FontAwesomeIcon icon={showPw ? faEyeSlash : faEye}/>
                                    </button>
                                </div>
                                {errors.password ? (
                                    <p className="authFieldError">{errors.password.message}</p>
                                ) : null}
                            </label>

                            <label className={`authField ${errors.confirm ? "authFieldInvalid" : ""}`}>
                                <span>Confirm password</span>
                                <div className="authInputRow">
                                    <input
                                        type={showConfirmPw ? "text" : "password"}
                                        placeholder="••••••••••••"
                                        disabled={anyLoading}
                                        {...register("confirm", {
                                            required: "Confirm password is required.",
                                            validate: (v) => v === passwordValue || "Passwords do not match.",
                                        })}
                                    />
                                    <button
                                        type="button"
                                        className="authPwToggle"
                                        onClick={() => setShowConfirmPw((s) => !s)}
                                        disabled={anyLoading}
                                        aria-label={showConfirmPw ? "Hide password" : "Show password"}
                                    >
                                        <FontAwesomeIcon icon={showConfirmPw ? faEyeSlash : faEye}/>
                                    </button>
                                </div>
                                {errors.confirm ? (
                                    <p className="authFieldError">{errors.confirm.message}</p>
                                ) : null}
                            </label>
                        </div>
                    ) : (
                        <label className={`authField ${errors.password ? "authFieldInvalid" : ""}`}>
                            <span>Password</span>
                            <div className="authInputRow">
                                <input
                                    type={showPw ? "text" : "password"}
                                    placeholder="••••••••••••"
                                    disabled={anyLoading}
                                    {...register("password", {
                                        required: "Password is required.",
                                        minLength: {value: 6, message: "Password must be at least 6 characters."},
                                    })}
                                />
                                <button
                                    type="button"
                                    className="authPwToggle"
                                    onClick={() => setShowPw((s) => !s)}
                                    disabled={anyLoading}
                                    aria-label={showPw ? "Hide password" : "Show password"}
                                >
                                    <FontAwesomeIcon icon={showPw ? faEyeSlash : faEye}/>
                                </button>
                            </div>
                            {errors.password ? (
                                <p className="authFieldError">{errors.password.message}</p>
                            ) : null}
                        </label>
                    )}

                    {mode === "signin" && (
                        <div className="authForgotRow">
                            <button type="button" className="authForgotLink" onClick={handleForgotPassword} disabled={anyLoading}>
                                Forgot Password?
                            </button>
                        </div>
                    )}

                    <button className="authSubmit" type="submit" disabled={anyLoading}>
                        {formLoading ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin/> Please wait…
                            </>
                        ) : mode === "signin" ? "Login" : "Create account"}
                    </button>

                    <div className="authDivider">
                        <span>OR</span>
                    </div>

                    <div className="authSocialButtons">
                        <button
                            type="button"
                            className="authSocialBtn"
                            onClick={handleGoogleSignIn}
                            disabled={anyLoading}
                        >
                            {socialLoading === "google" ? <FontAwesomeIcon icon={faSpinner} spin/> : <GoogleIcon/>}
                            <span>With Google</span>
                        </button>
                        <button
                            type="button"
                            className="authSocialBtn"
                            onClick={handleFacebookSignIn}
                            disabled={anyLoading}
                        >
                            {socialLoading === "facebook" ? (
                                <FontAwesomeIcon icon={faSpinner} spin/>
                            ) : (
                                <span className="authFbBadge"><FontAwesomeIcon icon={faFacebookF}/></span>
                            )}
                            <span>With Facebook</span>
                        </button>
                    </div>

                    <div className="authHint">
                        {mode === "signin" ? (
                            <>
                                Don&apos;t have an account?
                                <button type="button" className="authLink" onClick={() => switchMode("signup")} disabled={anyLoading}>
                                    Register now
                                </button>
                            </>
                        ) : (
                            <>
                                Already have an account?
                                <button type="button" className="authLink" onClick={() => switchMode("signin")} disabled={anyLoading}>
                                    Sign in
                                </button>
                            </>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
}
